import React, { useCallback, useEffect, useRef } from 'react';
import { CLIENT_ERROR_FIELD, HONEYPOT_FIELD, TOKEN_FIELD } from '@shared/botCheckFields';
import './index.scss';

/**
 * The bot check on the sign-in form (see backend/src/helpers/botCheck.ts for why and for the
 * server side). A port of the public site's `useBotCheck`, so the two behave the same way.
 *
 * `useBotCheck()` gives the form an element to render inside it, and `fields()` to await on submit
 * and spread into the request body. The widget is Cloudflare Turnstile in Managed mode: it usually
 * passes in the background, and when it is unsure it draws a "Verify you are human" checkbox into
 * its mount. So the mount is a visible part of the form, hidden only while it is empty.
 *
 * Tokens are single-use, so `fields()` hands the current one over and asks Cloudflare for the next
 * straight away: a retry after a wrong password finds a fresh token waiting.
 *
 * With no site key (local development, or a build without VITE_TURNSTILE_SITE_KEY) nothing loads
 * and only the honeypot is sent; the BFF skips Turnstile when it has no secret, so sign-in works.
 */

/**
 * Public, and fixed at build time: Vite inlines it from the VITE_TURNSTILE_SITE_KEY build arg (see
 * the Dockerfile). Written as the plain `import.meta.env.X` expression, not through a cast as
 * firebaseMessaging's IS_PROD is: Vitest substitutes only the literal text, so the cast form read
 * undefined under test. Typed in declarations.d.ts. Read per call so a test can stub it.
 */
const siteKey = (): string => import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/**
 * How long submit waits for a token the widget has not produced yet. 15s, not less: on the public
 * site a real visitor on a mid-range phone submitted with no token at all at 8s, because loading
 * the script and running the challenge took most of that.
 */
export const TOKEN_WAIT_MS = 15000;

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi | null> | null = null;

/** Loads Cloudflare's script once per page. */
function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof window === 'undefined' || !siteKey()) return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile ?? null);
      s.onerror = () => {
        scriptPromise = null;
        resolve(null);
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/** The console's own theme, which can differ from the OS preference Turnstile's `auto` reads. */
const consoleTheme = () =>
  document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';

export interface BotCheck {
  /** Render this inside the <form>. */
  element: React.ReactNode;
  /** The fields to send with the request: the token, and the honeypot's value. */
  fields: () => Promise<Record<string, string>>;
  /** Discard the current token and ask for a fresh one. `fields()` already does this. */
  reset: () => void;
}

export function useBotCheck(action: string): BotCheck {
  const mountRef = useRef<HTMLDivElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const widgetId = useRef<string | null>(null);
  const token = useRef<string>('');
  /** Cloudflare's last error code, sent with a token-less submit so the server log says why. */
  const lastError = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    void loadTurnstile().then((api) => {
      if (cancelled || !api || !mountRef.current || widgetId.current) return;
      widgetId.current = api.render(mountRef.current, {
        sitekey: siteKey(),
        action,
        theme: consoleTheme(),
        callback: (t: string) => {
          token.current = t;
          lastError.current = '';
        },
        'expired-callback': () => {
          token.current = '';
        },
        'error-callback': (code: string) => {
          token.current = '';
          lastError.current = String(code ?? 'unknown');
        },
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [action]);

  const reset = useCallback(() => {
    token.current = '';
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  const fields = useCallback(async (): Promise<Record<string, string>> => {
    const honeypot = honeypotRef.current?.value ?? '';
    if (!siteKey()) return { [HONEYPOT_FIELD]: honeypot };
    const started = Date.now();
    while (!token.current && Date.now() - started < TOKEN_WAIT_MS) {
      await new Promise((r) => setTimeout(r, 150));
    }
    const sent = token.current;
    if (!sent) {
      // Nothing to spend, so keep the widget running rather than restarting its challenge: a
      // retry then gets the token as soon as it lands, instead of waiting from zero again.
      return {
        [TOKEN_FIELD]: '',
        [HONEYPOT_FIELD]: honeypot,
        [CLIENT_ERROR_FIELD]: lastError.current || 'no-token-yet',
      };
    }
    reset();
    return { [TOKEN_FIELD]: sent, [HONEYPOT_FIELD]: honeypot };
  }, [reset]);

  const element = (
    <>
      <div ref={mountRef} className="bot-check-widget" data-testid="bot-check-widget" />
      <div aria-hidden="true" className="bot-check-honeypot">
        <label htmlFor={`${action}-${HONEYPOT_FIELD}`}>Leave this field empty</label>
        <input
          ref={honeypotRef}
          autoComplete="off"
          defaultValue=""
          id={`${action}-${HONEYPOT_FIELD}`}
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          type="text"
        />
      </div>
    </>
  );

  return { element, fields, reset };
}
